/**
 * SeatAssignmentModal.tsx
 * 좌석 배정 확인 모달
 *
 * Features:
 * - 학생 정보 표시 (이름, 사진, 이용권)
 * - 선택된 좌석 정보
 * - 이전 좌석 자동 해제 옵션
 * - 배정 확인 및 취소
 */

import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';

interface Student {
  id: string;
  name: string;
  phone: string;
  profile_image_url?: string;
  ticket_name?: string;
  ticket_expires_at?: string;
}

interface SeatInfo {
  seat_id: string;
  seat_code: string;
  floor_name: string;
}

interface SeatAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  selectedSeat: SeatInfo | null;
  currentSeat?: {
    seat_code: string;
    floor_name: string;
  } | null;
  onConfirm: (autoReleasePrevious: boolean) => void;
  loading?: boolean;
}

export const SeatAssignmentModal: React.FC<SeatAssignmentModalProps> = ({
  isOpen,
  onClose,
  student,
  selectedSeat,
  currentSeat,
  onConfirm,
  loading = false,
}) => {
  const [autoRelease, setAutoRelease] = React.useState(true);

  const handleConfirm = () => {
    onConfirm(autoRelease);
  };

  if (!student || !selectedSeat) {
    return null;
  }

  const hasCurrentSeat = !!currentSeat;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900"
                  >
                    좌석 배정 확인
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Student Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3 mb-3">
                    {student.profile_image_url ? (
                      <img
                        src={student.profile_image_url}
                        alt={student.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="w-12 h-12 text-gray-400" />
                    )}
                    <div>
                      <div className="font-medium text-gray-900">{student.name}</div>
                      <div className="text-sm text-gray-500">{student.phone}</div>
                    </div>
                  </div>

                  {student.ticket_name && (
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">이용권:</span> {student.ticket_name}
                      {student.ticket_expires_at && (
                        <span className="ml-2 text-gray-500">
                          (만료: {new Date(student.ticket_expires_at).toLocaleDateString()})
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Seat Assignment Info */}
                <div className="space-y-3 mb-4">
                  {hasCurrentSeat && (
                    <div className="flex items-center justify-between py-2 px-3 bg-yellow-50 rounded-lg">
                      <span className="text-sm text-gray-600">현재 좌석</span>
                      <span className="font-medium text-gray-900">
                        {currentSeat.floor_name} - {currentSeat.seat_code}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-center py-2">
                    <svg
                      className="w-6 h-6 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 14l-7 7m0 0l-7-7m7 7V3"
                      />
                    </svg>
                  </div>

                  <div className="flex items-center justify-between py-2 px-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-gray-600">배정할 좌석</span>
                    <span className="font-medium text-blue-900">
                      {selectedSeat.floor_name} - {selectedSeat.seat_code}
                    </span>
                  </div>
                </div>

                {/* Auto Release Option */}
                {hasCurrentSeat && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={autoRelease}
                        onChange={e => setAutoRelease(e.target.checked)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <label className="text-sm font-medium text-gray-900">
                          기존 좌석 자동 해제
                        </label>
                        <p className="text-xs text-gray-600 mt-1">
                          현재 사용 중인 좌석을 자동으로 해제하고 새로운 좌석에 배정합니다.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Warning */}
                {hasCurrentSeat && !autoRelease && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-800">
                      기존 좌석을 해제하지 않으면 배정이 실패할 수 있습니다.
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={onClose}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 disabled:opacity-50"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '배정 중...' : '배정 확인'}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default SeatAssignmentModal;
